"use client";

import type { PlacePredictionsMeta } from "./GoogleLocationAutocomplete";

import { getPostalCodeFromCurrentLocation } from "@homeinstead/geolocation/browser";
import Loading from "@homeinstead/icons/Loading";
import LocationMe from "@homeinstead/icons/LocateMe";
import { getLocationSearchPlaceholder } from "@homeinstead/locale";
import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { useState } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { trackSearchResultClick, trackSearchSubmitButtonClick } from "./events";
import GoogleLocationAutoComplete from "./GoogleLocationAutocomplete";

export const SEARCH_BUTTON_FALLBACK = "Search";
export const USE_CURRENT_LOCATION = "Use my current location";

export type LocationSearchFormBreakpoint = "sm" | "lg" | "xl";

const breakpointClassMap: Record<
  LocationSearchFormBreakpoint,
  {
    form: string;
    location: string;
    buttonWrapper: string;
    button: string;
  }
> = {
  lg: {
    button: "lg:w-auto",
    buttonWrapper: "lg:order-2 lg:mt-0",
    form: "lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-4 lg:gap-y-3",
    location: "lg:order-3 lg:w-full",
  },
  sm: {
    button: "sm:w-auto",
    buttonWrapper: "sm:order-2 sm:mt-0",
    form: "sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-4 sm:gap-y-3",
    location: "sm:order-3 sm:w-full",
  },
  xl: {
    button: "xl:w-auto",
    buttonWrapper: "xl:order-2 xl:mt-0",
    form: "xl:flex-row xl:flex-wrap xl:items-start xl:gap-x-4 xl:gap-y-3",
    location: "xl:order-3 xl:w-full",
  },
};

export interface LocationSearchFormClassNames {
  label?: string;
  form?: string;
  currentLocation?: string;
  buttonWrapper?: string;
  button?: string;
}

export interface LocationSearchFormProps {
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  defaultValue?: string;
  onSubmit?: (value: string) => void;
  onSelect?: (value: string) => void;
  onInputChange?: (value: string) => void;
  buttonText?: string;
  placeholder?: string;
  fixedDropdown?: boolean;
  buttonVariant?: "primary" | "secondary" | "outline" | "friendly";
  noResultsMessage?: string;
  showCurrentLocation?: boolean;
  disableOnEmptyValue?: boolean;
  horizontalBreakpoint?: LocationSearchFormBreakpoint;
  className?: string;
  slotClassNames?: LocationSearchFormClassNames;
  onPredictionsMetaChange?: (meta: PlacePredictionsMeta) => void;
}

const LocationSearchForm = ({
  label,
  loading,
  onSubmit,
  onSelect,
  onInputChange,
  disabled,
  defaultValue = "",
  buttonText,
  placeholder,
  buttonVariant,
  fixedDropdown,
  noResultsMessage = "",
  showCurrentLocation = true,
  disableOnEmptyValue = false,
  horizontalBreakpoint = "sm",
  className,
  slotClassNames,
  onPredictionsMetaChange,
}: LocationSearchFormProps) => {
  const locale = useLocale();
  const locationPlaceholderFallback = getLocationSearchPlaceholder(
    locale.metadata.code,
  );

  const bp = breakpointClassMap[horizontalBreakpoint];
  const [value, setValue] = useState(defaultValue);
  const [noResultMessageInternal, setNoResultMessageInternal] =
    useState(noResultsMessage);
  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    trackSearchSubmitButtonClick();

    if (onSubmit) {
      onSubmit(value);
    }
  };

  const handleInputChange = (place: string): void => {
    setValue(place);
    setNoResultMessageInternal("");

    if (onInputChange) {
      onInputChange(place);
    }
  };

  const onSelectValue = (place: string): void => {
    setValue(place);
    trackSearchResultClick();
    if (onSelect) {
      onSelect(place);
    }
  };

  const handleUseCurrentLocation = async (): Promise<void> => {
    const postalCode = await getPostalCodeFromCurrentLocation();
    if (postalCode) {
      setValue(postalCode);
    }
  };

  const isButtonDisabled =
    disabled || loading || (disableOnEmptyValue && !value.trim());

  return (
    <div
      className={cn(
        "relative flex flex-col gap-4 text-sm leading-6",
        className,
      )}
    >
      {label ? (
        <span className={cn("font-bold text-md", slotClassNames?.label)}>
          {label}
        </span>
      ) : null}
      <form
        className={cn(
          "flex w-full flex-col gap-3",
          bp.form,
          slotClassNames?.form,
        )}
        onSubmit={handleSubmit}
      >
        <GoogleLocationAutoComplete
          disabled={disabled}
          fixedDropdown={fixedDropdown}
          onInputChange={handleInputChange}
          onPredictionsMetaChange={onPredictionsMetaChange}
          placeholder={placeholder || locationPlaceholderFallback}
          selectPlaceResult={onSelectValue}
          value={value}
        />

        {showCurrentLocation && (
          <Button
            className={cn(
              "order-2 flex h-auto cursor-pointer justify-normal gap-1 p-0 text-sm underline hover:no-underline",
              bp.location,
              slotClassNames?.currentLocation,
            )}
            onClick={handleUseCurrentLocation}
            type="button"
            variant="plain"
          >
            <LocationMe />
            <span className="text-new-beginnings-base">
              {USE_CURRENT_LOCATION}
            </span>
          </Button>
        )}

        <div
          className={cn(
            "order-3 mt-8",
            bp.buttonWrapper,
            slotClassNames?.buttonWrapper,
          )}
        >
          <Button
            className={cn(
              "w-full min-w-[144px] text-center font-semibold",
              bp.button,
              slotClassNames?.button,
            )}
            disabled={isButtonDisabled}
            loading={loading}
            type="submit"
            variant={buttonVariant}
          >
            {loading ? (
              <Loading className="h-8 w-8 fill-new-beginnings-base" />
            ) : (
              buttonText || SEARCH_BUTTON_FALLBACK
            )}
          </Button>
        </div>
      </form>
      {noResultMessageInternal && <div>{noResultMessageInternal}</div>}
    </div>
  );
};

export default LocationSearchForm;
